import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { LeftArw, RightArw, Search, ThreeDot } from "../../assests/ExportImageIcon";
import service from "../../services/service";
import endpoints from "../../services/endpoints";

const PAGE_SIZE = 10;

const Dashboard = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showResendSuccess, setShowResendSuccess] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [refetch, setRefetch] = useState(0);
  const dropdownRef = useRef(null);

  useEffect(() => {
    setPage(1);
  }, [searchQuery]);

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);
      try {
        const res = await service.get(endpoints.admin.userList, {
          page,
          page_size: PAGE_SIZE,
          ...(searchQuery ? { search: searchQuery } : {}),
        });
        const body = res.data;
        const list = Array.isArray(body?.data)
          ? body.data
          : body?.data?.results || [];
        setRows(list);
        const totalCount = body?.count ?? list.length;
        setTotalPages(Math.max(1, Math.ceil(totalCount / PAGE_SIZE)));
      } catch (err) {
        console.error("Failed to fetch users:", err);
      } finally {
        setLoading(false);
      }
    };

    const delay = searchQuery ? 400 : 0;
    const debounce = setTimeout(fetchUsers, delay);
    return () => clearTimeout(debounce);
  }, [page, searchQuery, refetch]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggleDropdown = (index) => {
    setActiveDropdown((prev) => (prev === index ? null : index));
  };

  const confirmDelete = async () => {
    setDeleting(true);
    try {
      const url = endpoints.admin.deleteUser.replace("{user_id}", deleteId);
      await service.delete(url);
      setDeleteId(null);
      setRefetch((n) => n + 1);
    } catch (err) {
      console.error("Failed to delete user:", err);
    } finally {
      setDeleting(false);
    }
  };

  const handleResendEmail = async (userId) => {
    setActiveDropdown(null);
    try {
      const url = endpoints.admin.sendlink.replace("{user_id}", userId);
      await service.post(url, {});
      setShowResendSuccess(true);
      setTimeout(() => setShowResendSuccess(false), 2500);
    } catch (err) {
      console.error("Failed to send link:", err);
    }
  };

  return (
    <>
      {showResendSuccess && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <div className="success-icon">✓</div>
            <h3>Email Resent!</h3>
            <p>Chat link has been sent successfully.</p>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="confirm-overlay">
          <div className="confirm-modal">
            <h3>Delete User</h3>
            <p>Are you sure you want to delete this user?</p>
            <div className="confirm-actions">
              <button className="confirm-cancel" onClick={() => setDeleteId(null)} disabled={deleting}>
                Cancel
              </button>
              <button className="confirm-ok commonOrngBtn" onClick={confirmDelete} disabled={deleting}>
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="dashboard-page commonBorder commonRadius-12 bg-white">
        <div className="topPart">
          <div className="heading">User Listing</div>
          <div className="rightSide">
            <div className="search">
              <img src={Search} alt="search" />
              <input
                type="search"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>
            <button className="commonOrngBtn" onClick={() => navigate("/dashboard/add-user")}>+ Add User</button>
          </div>
        </div>

        <div className="tablePart table-responsive" ref={dropdownRef}>
          <table>
            <thead>
              <tr>
                <th>Customer Name</th>
                <th>Email ID</th>
                <th>Property Type</th>
                <th>Monthly Budget</th>
                <th className="actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5}>
                    <div className="tableLoader">
                      <div className="tableSpinner"></div>
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={5} style={{ textAlign: "center", padding: "30px" }}>No users found</td>
                </tr>
              ) : (
                rows.map((row, index) => (
                  <tr key={row.id || index}>
                    <td>{row.customerName || "-"}</td>
                    <td>{row.email || "-"}</td>
                    <td>{row.propertyType || "-"}</td>
                    <td>{row.monthlyBudget || "-"}</td>
                    <td className="action">
                      <div
                        className="threeDot commonBorder commonRadius-12"
                        onClick={() => toggleDropdown(index)}
                      >
                        <img src={ThreeDot} alt="options" />
                      </div>
                      {activeDropdown === index && (
                        <div className="dropdwn">
                          <ul>
                            <li><span onClick={() => navigate("/dashboard/user-detail", { state: { userId: row.id } })}>View Detail</span></li>
                            <li><span onClick={() => navigate("/dashboard/add-user", { state: { userId: row.id } })}>Edit</span></li>
                            <li><span onClick={() => handleResendEmail(row.id)}>Re-Send Email</span></li>
                            <li><span onClick={() => { setActiveDropdown(null); setDeleteId(row.id); }}>Delete User</span></li>
                          </ul>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <div className="pagination">
          <ul>
            <li>
              <div
                className={`btnAction ${page === 1 ? "disabled" : ""}`}
                onClick={() => page > 1 && setPage((p) => p - 1)}
              >
                <img src={LeftArw} />
              </div>
            </li>
            {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
              <li key={p}>
                <div
                  className={`btnAction ${p === page ? "active" : ""}`}
                  onClick={() => setPage(p)}
                >
                  {p}
                </div>
              </li>
            ))}
            <li>
              <div
                className={`btnAction ${page === totalPages ? "disabled" : ""}`}
                onClick={() => page < totalPages && setPage((p) => p + 1)}
              >
                <img src={RightArw} />
              </div>
            </li>
          </ul>
        </div>
      </div>
    </>
  );
};

export default Dashboard;
